import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import type { KeyboardEvent } from 'react'
import type { DateRange } from 'react-day-picker'

import { format, isValid, parse, subMonths } from 'date-fns'

import { Flex, Input, SearchIcon, Text } from '@buffer-mono/popcorn'

import { DateRangePicker } from './DateRangePicker'
import { parseDateRangeQuery } from './parseDateRangeQuery'

import styles from './DateRangePopover.module.css'

const UNPARSEABLE_QUERY_MESSAGE =
  'Sorry, we couldn’t process that date range. Try something like “last 30 days”, “April 2026”, or “April to May 2026”.'

const DATE_DISPLAY_FORMAT = 'MMM d, yyyy'
const PARSE_FORMATS = [DATE_DISPLAY_FORMAT, 'M/d/yyyy', 'yyyy-MM-dd']

function parseDate(text: string): Date | null {
  const ref = new Date()
  for (const fmt of PARSE_FORMATS) {
    const parsed = parse(text.trim(), fmt, ref)
    if (isValid(parsed)) {
      if (parsed.getFullYear() < 100) {
        const corrected = parsed.getFullYear() + 2000
        if (Math.abs(corrected - ref.getFullYear()) > 10) continue
        parsed.setFullYear(corrected)
      }
      return parsed
    }
  }
  return null
}

function formatDate(date: Date | undefined): string {
  return date ? format(date, DATE_DISPLAY_FORMAT) : ''
}

interface UseDateRangePickerStateOptions {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialRange?: { startDate: Date; endDate: Date }
  onApply: (startDate: Date, endDate: Date) => void
  weekStartsOn?: 0 | 1
  /** When true, shows a single month and doesn't offset currentMonth by 1 */
  singleMonth?: boolean
}

export interface DateRangePickerState {
  pendingRange: DateRange | undefined
  pickerKey: number
  currentMonth: Date
  setCurrentMonth: (month: Date) => void
  isSelectingEnd: boolean
  startText: string
  setStartText: (text: string) => void
  endText: string
  setEndText: (text: string) => void
  searchText: string
  setSearchText: (text: string) => void
  searchError: string | null
  maxDate: Date
  handleSelect: (range: DateRange | undefined, triggerDate: Date) => void
  handleStartBlur: () => void
  handleEndBlur: () => void
  handleInputKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
  commitSearch: () => void
  handleSearchKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
  handleApply: () => void
  handleCancel: () => void
  isApplyDisabled: boolean
  applyDisabledReason: string | null
}

export function useDateRangePickerState({
  open,
  onOpenChange,
  initialRange,
  onApply,
  weekStartsOn,
  singleMonth = false,
}: UseDateRangePickerStateOptions): DateRangePickerState {
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(
    initialRange
      ? { from: initialRange.startDate, to: initialRange.endDate }
      : undefined,
  )
  // Key to force remount of DateRangePicker when resetting from a complete range,
  // so react-day-picker's internal state stays in sync with our pendingRange.
  const [pickerKey, setPickerKey] = useState(0)
  // Controlled month for the left/only calendar panel — persists across picker remounts
  // so the view doesn't jump when the user picks a start date.
  // In single-month mode (drawer), show the end date's month directly.
  // In two-month mode (popover), offset by 1 so the right panel shows the end month.
  const [currentMonth, setCurrentMonth] = useState<Date>(() =>
    singleMonth
      ? (initialRange?.endDate ?? new Date())
      : subMonths(initialRange?.endDate ?? new Date(), 1),
  )
  // Tracks whether the user has picked a start date and is now picking the end
  const [isSelectingEnd, setIsSelectingEnd] = useState(false)

  const [startText, setStartText] = useState(() =>
    formatDate(initialRange?.startDate),
  )
  const [endText, setEndText] = useState(() =>
    formatDate(initialRange?.endDate),
  )
  const [searchText, setSearchText] = useState('')
  const [searchError, setSearchError] = useState<string | null>(null)

  // Escape resets the text and calls .blur() to leave the field, but that
  // blur fires the same commit handler that a normal blur would — reading
  // the pre-reset text from this render's closure, since the state update
  // that clears it hasn't been committed yet. Without this guard, Escape
  // would "cancel" visually while still committing what was typed.
  const skipNextCommitRef = useRef(false)

  const updateSearchText = (text: string): void => {
    setSearchText(text)
    if (searchError) setSearchError(null)
  }

  // Keep text inputs in sync when the calendar selection changes
  useEffect(() => {
    setStartText(formatDate(pendingRange?.from))
  }, [pendingRange?.from])

  useEffect(() => {
    setEndText(formatDate(pendingRange?.to))
  }, [pendingRange?.to])

  useEffect(() => {
    if (open && initialRange) {
      setPendingRange({
        from: initialRange.startDate,
        to: initialRange.endDate,
      })
      setCurrentMonth(
        singleMonth ? initialRange.endDate : subMonths(initialRange.endDate, 1),
      )
      setPickerKey((k) => k + 1)
      setSearchText('')
      setSearchError(null)
    }
    if (!open) {
      setIsSelectingEnd(false)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (
    _range: DateRange | undefined,
    triggerDate: Date,
  ): void => {
    if (!isSelectingEnd) {
      // First click — start a new selection
      setPendingRange({ from: triggerDate, to: triggerDate })
      setPickerKey((k) => k + 1)
      setIsSelectingEnd(true)
    } else {
      // Second click — complete the range
      setPendingRange(_range)
      setIsSelectingEnd(false)
    }
  }

  // Stable reference — today's date doesn't change within a session, and a new Date()
  // on every render would make DayPicker's disabled array referentially new each time.
  const maxDate = useMemo(() => new Date(), [])

  const commitDateInput = (
    field: 'from' | 'to',
    text: string,
    revert: () => void,
  ): void => {
    const parsed = parseDate(text)
    if (!parsed || parsed > maxDate) {
      revert()
      return
    }
    setPendingRange((prev) => {
      const updated: DateRange =
        field === 'from'
          ? { from: parsed, to: prev?.to }
          : { from: prev?.from, to: parsed }
      // Normalize so from <= to (swap if needed)
      if (updated.from && updated.to && updated.from > updated.to) {
        return { from: updated.to, to: updated.from }
      }
      return updated
    })
    // In single-month mode, navigate to the typed date's month directly.
    // In two-month mode, offset the "to" field by 1 so it shows on the right panel.
    setCurrentMonth(
      singleMonth ? parsed : subMonths(parsed, field === 'to' ? 1 : 0),
    )
  }

  const handleStartBlur = (): void => {
    if (skipNextCommitRef.current) {
      skipNextCommitRef.current = false
      return
    }
    commitDateInput('from', startText, () =>
      setStartText(formatDate(pendingRange?.from)),
    )
  }

  const handleEndBlur = (): void => {
    if (skipNextCommitRef.current) {
      skipNextCommitRef.current = false
      return
    }
    commitDateInput('to', endText, () =>
      setEndText(formatDate(pendingRange?.to)),
    )
  }

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      skipNextCommitRef.current = true
      setStartText(formatDate(pendingRange?.from))
      setEndText(formatDate(pendingRange?.to))
      e.currentTarget.blur()
    }
  }

  const commitSearch = (): void => {
    if (skipNextCommitRef.current) {
      skipNextCommitRef.current = false
      return
    }
    if (!searchText.trim()) return
    const result = parseDateRangeQuery(searchText, maxDate, weekStartsOn)
    if (result) {
      setPendingRange(result)
      if (result.to)
        setCurrentMonth(singleMonth ? result.to : subMonths(result.to, 1))
      setPickerKey((k) => k + 1)
      setIsSelectingEnd(false)
      setSearchText('')
      setSearchError(null)
    } else {
      setSearchError(UNPARSEABLE_QUERY_MESSAGE)
    }
  }

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      commitSearch()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      skipNextCommitRef.current = true
      setSearchText('')
      setSearchError(null)
      e.currentTarget.blur()
    }
  }

  const handleApply = (): void => {
    if (pendingRange?.from && pendingRange?.to) {
      onApply(pendingRange.from, pendingRange.to)
      onOpenChange(false)
    }
  }

  const handleCancel = (): void => {
    onOpenChange(false)
  }

  const applyDisabledReason =
    isSelectingEnd ||
    !pendingRange?.from ||
    !pendingRange?.to ||
    pendingRange.from.getTime() === pendingRange.to.getTime()
      ? 'Select a start and end date to apply'
      : null

  return {
    pendingRange,
    pickerKey,
    currentMonth,
    setCurrentMonth,
    isSelectingEnd,
    startText,
    setStartText,
    endText,
    setEndText,
    searchText,
    setSearchText: updateSearchText,
    searchError,
    maxDate,
    handleSelect,
    handleStartBlur,
    handleEndBlur,
    handleInputKeyDown,
    commitSearch,
    handleSearchKeyDown,
    handleApply,
    handleCancel,
    isApplyDisabled: applyDisabledReason !== null,
    applyDisabledReason,
  }
}

interface DateRangePickerContentProps {
  state: DateRangePickerState
  weekStartsOn?: 0 | 1
  numberOfMonths?: 1 | 2
  fullWidth?: boolean
}

/** Renders the search input and calendar. Used by both DateRangePopover and DateRangeDrawer. */
export function DateRangePickerContent({
  state,
  weekStartsOn,
  numberOfMonths = 2,
  fullWidth = false,
}: DateRangePickerContentProps): JSX.Element {
  const searchErrorId = useId()

  return (
    <>
      <Input
        size="medium"
        className={styles.searchInput}
        prefix={<SearchIcon />}
        placeholder="Try: last 7 days, this month, Q1 2026..."
        value={state.searchText}
        onChange={(e) => state.setSearchText(e.target.value)}
        onBlur={state.commitSearch}
        onKeyDown={state.handleSearchKeyDown}
        aria-invalid={state.searchError ? true : undefined}
        aria-describedby={state.searchError ? searchErrorId : undefined}
      />
      {state.searchError && (
        <Text
          id={searchErrorId}
          as="p"
          size="sm"
          color="critical"
          className={styles.searchError}
        >
          {state.searchError}
        </Text>
      )}
      <DateRangePicker
        key={state.pickerKey}
        selected={state.pendingRange}
        onSelect={state.handleSelect}
        month={state.currentMonth}
        onMonthChange={state.setCurrentMonth}
        weekStartsOn={weekStartsOn}
        maxDate={state.maxDate}
        numberOfMonths={numberOfMonths}
        fullWidth={fullWidth}
      />
    </>
  )
}

interface DateRangePickerDateInputsProps {
  state: DateRangePickerState
}

/** Renders the start/end date text inputs. Used by both DateRangePopover and DateRangeDrawer. */
export function DateRangePickerDateInputs({
  state,
}: DateRangePickerDateInputsProps): JSX.Element {
  return (
    <Flex align="center" gap="xs" wrap="wrap">
      <Input
        className={styles.dateInput}
        value={state.startText}
        placeholder="Start date"
        onChange={(e) => state.setStartText(e.target.value)}
        onBlur={state.handleStartBlur}
        onKeyDown={state.handleInputKeyDown}
      />
      <Text size="sm" color="subtle">
        to
      </Text>
      <Input
        className={styles.dateInput}
        value={state.endText}
        placeholder="End date"
        onChange={(e) => state.setEndText(e.target.value)}
        onBlur={state.handleEndBlur}
        onKeyDown={state.handleInputKeyDown}
      />
    </Flex>
  )
}
